/**
 * One live class of a cohort module: its recording (protected player), what
 * it covered, and the module's other classes for quick switching.
 *
 * Routes: /dashboard/batch/:batchId/sessions/:sessionId   (mentor)
 *         /dashboard/workspace/:id/sessions/:sessionId    (student)
 *
 * The side column: Ask AI (the batch tutor, asked about the moment being
 * watched), the module's sessions, and — for the mentor — every student's
 * questions. `?r=<recording id>&t=<seconds>` opens a recording at a moment
 * (a tutor answer's "Class 2 · 1:12:30" from another class's page).
 *
 * On wide screens the page uses the full width: the recording and the side
 * panel split 70/30, the divider drags either way, and the panel collapses to
 * a thin rail. Both are remembered (localStorage `session-layout`).
 *
 * Theatre mode (wide screens): the same two columns take the whole window —
 * the video fills its column top to bottom, Ask AI stays beside it. It is the
 * same elements restyled, not new ones, so playback and a question being
 * answered carry straight on. Esc or the player's button leaves it.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import VideoCallOutlinedIcon from "@mui/icons-material/VideoCallOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";

import NavbarLoggedInNew from "../../components/Navbar-new/NavbarLoggedInNew";
import { ConfirmDialog } from "../ContentBank/dialogs";
import { formatClock } from "./SecurePlayer";
import {
  EditSessionDialog,
  FONT,
  STATUS,
  ScheduleDialog,
  SessionBody,
  ViewersDialog,
  btn,
  fmtDay,
  fmtWhen,
} from "./sessionParts";
import { totalLength, watchedPercent } from "./SessionTracking";
import SessionTutor from "./SessionTutor";
import StudentQuestions from "./StudentQuestions";
import {
  deleteRecording,
  deleteSession,
  errorMessage,
  getSession,
  getUploadUrl,
  registerRecording,
  reprocessRecording,
  updateSession,
  uploadFile,
} from "./sessionsApi";

const POLL_MS = 60000;

// The recording's share of the width, and whether the side panel is folded away.
const LAYOUT_KEY = "session-layout";
export const DEFAULT_SPLIT = 70;
export const MIN_SPLIT = 30;   // the panel can take up to 70%
export const MAX_SPLIT = 85;   // or shrink to 15%
export const clampSplit = (v) => Math.min(MAX_SPLIT, Math.max(MIN_SPLIT, Number.isFinite(v) ? v : DEFAULT_SPLIT));
const readLayout = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(LAYOUT_KEY) || "{}");
    return { split: clampSplit(Number(saved.split ?? DEFAULT_SPLIT)), collapsed: !!saved.collapsed, theatre: !!saved.theatre };
  } catch {
    return { split: DEFAULT_SPLIT, collapsed: false, theatre: false };
  }
};
const saveLayout = (layout) => {
  try { localStorage.setItem(LAYOUT_KEY, JSON.stringify(layout)); } catch { /* not remembered, still works */ }
};

/** The draggable bar between the recording and the panel (arrow keys move it too; double-click resets). */
function Divider({ split, onChange, containerRef }) {
  const [dragging, setDragging] = useState(false);
  const onPointerDown = (e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDragging(true);
  };
  const onPointerMove = (e) => {
    if (!dragging || !containerRef.current) return;
    const box = containerRef.current.getBoundingClientRect();
    if (box.width > 0) onChange(clampSplit(((e.clientX - box.left) / box.width) * 100));
  };
  const stop = () => setDragging(false);
  useEffect(() => {
    if (!dragging) return undefined;
    const previous = document.body.style.userSelect;
    document.body.style.userSelect = "none";          // no text selection while dragging
    return () => { document.body.style.userSelect = previous; };
  }, [dragging]);
  return (
    <Box role="separator" aria-orientation="vertical" aria-label="Resize the video and the panel"
      aria-valuenow={Math.round(split)} aria-valuemin={MIN_SPLIT} aria-valuemax={MAX_SPLIT} tabIndex={0}
      onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={stop} onPointerCancel={stop}
      onDoubleClick={() => onChange(DEFAULT_SPLIT)}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") { e.preventDefault(); onChange(clampSplit(split - 2)); }
        if (e.key === "ArrowRight") { e.preventDefault(); onChange(clampSplit(split + 2)); }
      }}
      title="Drag to resize · double-click to reset"
      sx={{
        width: 16, flexShrink: 0, cursor: "col-resize", display: "flex", alignItems: "center", justifyContent: "center",
        alignSelf: "stretch", touchAction: "none", outline: "none",
        "&:hover .grip, &:focus-visible .grip": { backgroundColor: "#0172A6", height: 64 },
      }}>
      <Box className="grip" sx={{ width: 4, height: 40, borderRadius: 2, backgroundColor: dragging ? "#0172A6" : "#cbd5e1",
        transition: "height 0.15s ease, background-color 0.15s ease", position: "sticky", top: "50vh" }} />
    </Box>
  );
}

export default function SessionPage() {
  const { batchId, id: courseId, sessionId } = useParams();
  const navigate = useNavigate();
  const profile = useSelector((state) => state.userProfile?.data);
  const viewer = useMemo(() => ({
    name: [profile?.first_name, profile?.last_name].filter(Boolean).join(" "),
    email: profile?.email,
  }), [profile]);

  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [part, setPart] = useState(0);
  const [dialog, setDialog] = useState(null); // edit | reschedule | cancel | viewers
  const [busy, setBusy] = useState(false);
  const [upload, setUpload] = useState(null);
  const [toast, setToast] = useState("");
  const fileInput = useRef(null);
  const playerRef = useRef(null);
  const [side, setSide] = useState("ai"); // ai | sessions | students
  // A moment to open at, from a tutor answer: {recordingId, t}. Applied once, to that recording only.
  const [jump, setJump] = useState(null);
  const [searchParams] = useSearchParams();
  const { key: locationKey } = useLocation();
  const linked = useRef(null);
  const wide = useMediaQuery("(min-width:900px)");
  const [layout, setLayout] = useState(readLayout);
  const splitRef = useRef(null);
  const updateLayout = useCallback((change) => {
    setLayout((cur) => {
      const next = { ...cur, ...change };
      saveLayout(next);
      return next;
    });
  }, []);
  const theatre = wide && layout.theatre;
  const toggleTheatre = useCallback(() => updateLayout({ theatre: !layout.theatre }), [layout.theatre, updateLayout]);
  useEffect(() => {
    if (!theatre) return undefined;
    const onKey = (e) => { if (e.key === "Escape" && !document.fullscreenElement) updateLayout({ theatre: false }); };
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";           // the page behind doesn't scroll
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = previous; };
  }, [theatre, updateLayout]);

  const backPath = batchId ? `/dashboard/batch/${batchId}/workspace` : `/dashboard/workspace/${courseId}`;
  const sessionPath = (sid) => (batchId ? `/dashboard/batch/${batchId}/sessions/${sid}` : `/dashboard/workspace/${courseId}/sessions/${sid}`);

  const load = useCallback(async () => {
    try {
      setData(await getSession(sessionId));
      setLoadError("");
    } catch (error) {
      setLoadError(errorMessage(error, "Couldn't load this session"));
    }
  }, [sessionId]);

  useEffect(() => { setData(null); setPart(0); setJump(null); load(); }, [load]);

  const editor = !!data?.can_edit;
  const oneToOne = data?.module?.kind === "topic";
  const sessions = useMemo(() => data?.sessions || [], [data]);
  const session = sessions.find((s) => s.id === Number(sessionId)) || null;
  const recordings = useMemo(() => session?.recordings || [], [session]);
  const recording = recordings[Math.min(part, Math.max(0, recordings.length - 1))];

  // A link to a moment: pick that recording's part and start there, once per link.
  useEffect(() => {
    const r = Number(searchParams.get("r"));
    const t = Number(searchParams.get("t"));
    // Keyed by this navigation, so following the same link again works again.
    if (!r || !recordings.length || linked.current === locationKey) return;
    const index = recordings.findIndex((x) => x.id === r);
    if (index >= 0) {
      linked.current = locationKey;
      if (playerRef.current?.recordingId === r && Number.isFinite(t)) {
        playerRef.current.seek(t);                  // already playing: its start position is long past
        return;
      }
      setPart(index);
      setJump(Number.isFinite(t) ? { recordingId: r, t } : null);
    }
  }, [searchParams, recordings, locationKey]);

  // A tutor answer's source: jump within this class, or open the other class at that moment.
  const openSource = useCallback((src) => {
    if (src.kind !== "recording") return;
    if (src.session_id === Number(sessionId)) {
      const index = recordings.findIndex((x) => x.id === src.recording_id);
      if (index < 0) {
        setToast("That recording isn't available any more.");
      } else if (recordings[index].id !== recording?.id) {
        setPart(index);
        setJump({ recordingId: src.recording_id, t: src.start });
      } else {
        playerRef.current?.seek(src.start);
      }
    } else {
      navigate(`${sessionPath(src.session_id)}?r=${src.recording_id}&t=${Math.floor(src.start)}`);
    }
  }, [sessionId, recordings, recording, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!session || !["upcoming", "live", "awaiting_recording", "processing"].includes(session.status)) return undefined;
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [session, load]);

  const run = async (action, success, after) => {
    setBusy(true);
    try {
      const payload = await action();
      if (payload?.sessions) setData({ ...payload, session_id: Number(sessionId) });
      setDialog(null);
      if (success) setToast(success);
      if (after) after();
    } catch (error) {
      setToast(errorMessage(error, "Something went wrong"));
    } finally {
      setBusy(false);
    }
  };

  const onUploadPicked = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !session) return;
    setUpload({ name: file.name, progress: 0 });
    try {
      const target = await getUploadUrl(session.id, file);
      await uploadFile(target.upload_url, file, target.content_type, (progress) => setUpload({ name: file.name, progress }));
      setData({ ...(await registerRecording(session.id, target.key, file.name)), session_id: session.id });
      setToast("Uploaded. It will be ready to stream in a few minutes.");
    } catch (error) {
      setToast(errorMessage(error, "Upload failed"));
    } finally {
      setUpload(null);
    }
  };

  const onProgress = useCallback((recordingId, result) => {
    setData((prev) => prev && ({
      ...prev,
      sessions: prev.sessions.map((s) => ({
        ...s,
        recordings: s.recordings.map((r) => (r.id === recordingId ? { ...r, my_percent: result.percent, my_position: result.position } : r)),
      })),
    }));
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f8fafc" }}>
      <NavbarLoggedInNew />
      <Box sx={{ px: { xs: 2, sm: 3 }, pt: 1.5, pb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(backPath)} sx={{ ...btn, color: "#374151", mb: 1 }}>
          {data?.module?.name || "Back"}
        </Button>

        {loadError && (
          <Paper sx={{ p: 3, borderRadius: 3 }} elevation={0}>
            <Typography sx={{ color: "#b91c1c", fontFamily: FONT }}>{loadError}</Typography>
            <Button onClick={load} sx={btn}>Try again</Button>
          </Paper>
        )}
        {!data && !loadError && <LinearProgress />}
        {data && !session && (
          <Paper sx={{ p: 3, borderRadius: 3 }} elevation={0}>
            <Typography sx={{ fontFamily: FONT }}>This session is no longer available.</Typography>
          </Paper>
        )}

        {session && (
          <Box ref={splitRef} data-testid="session-split" sx={{ display: "flex", flexDirection: wide ? "row" : "column", gap: wide ? 0 : 3,
            alignItems: theatre ? "stretch" : "flex-start",
            ...(theatre ? { position: "fixed", inset: 0, zIndex: 1250, backgroundColor: "#0b0d12", p: 1 } : {}) }}>
            {/* Main: header + recording */}
            <Box sx={{ width: wide ? (layout.collapsed ? "calc(100% - 56px)" : `${layout.split}%`) : "100%", minWidth: 0,
              flexShrink: 0, pr: wide && layout.collapsed ? 2 : 0 }}>
              <Paper elevation={0} sx={theatre
                ? { p: 0, height: "100%", borderRadius: 2, backgroundColor: "transparent", display: "flex", flexDirection: "column" }
                : { p: { xs: 2, sm: 3 }, borderRadius: 3, border: "1px solid #e5e7eb" }}>
                <Box sx={{ display: theatre ? "none" : "block" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
                  <Chip size="small" label={STATUS[session.status]?.label} color={STATUS[session.status]?.color} />
                  {session.starts_at && (
                    <Typography sx={{ fontSize: 13, color: "#4b5563", fontFamily: FONT }}>
                      <EventOutlinedIcon sx={{ fontSize: 16, verticalAlign: "text-bottom", mr: 0.5 }} />
                      {session.ends_at ? fmtWhen(session.starts_at) : fmtDay(session.starts_at)}
                    </Typography>
                  )}
                  {totalLength(session) > 0 && (
                    <Typography sx={{ fontSize: 13, color: "#4b5563", fontFamily: FONT }}>· {formatClock(totalLength(session))}</Typography>
                  )}
                  <Box sx={{ flex: 1 }} />
                  {editor && (
                    <>
                      <Tooltip title="Edit name, description or date">
                        <IconButton size="small" onClick={() => setDialog("edit")}><DriveFileRenameOutlineIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Upload a recording">
                        <span>
                          <IconButton size="small" onClick={() => fileInput.current?.click()} disabled={!!upload}>
                            <FileUploadOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={session.status === "upcoming" ? "Cancel session" : "Remove session"}>
                        <IconButton size="small" onClick={() => setDialog("cancel")}><DeleteOutlineIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <input ref={fileInput} type="file" hidden accept="video/mp4,video/quicktime,.mp4,.m4v,.mov" onChange={onUploadPicked}
                        data-testid="recording-input" />
                    </>
                  )}
                </Box>

                <Typography component="h1" sx={{ fontFamily: FONT, fontWeight: 600, fontSize: { xs: 20, sm: 24 }, color: "#111827", lineHeight: 1.3 }}>
                  Session {session.number}{session.title ? ` · ${session.title}` : ""}
                </Typography>
                {session.shared_from && (
                  <Typography sx={{ fontSize: 13, color: "#6b7280", fontFamily: FONT, mt: 0.5 }}>
                    Shared from {session.shared_from}'s card{editor ? ": they study together. Changes made there follow here." : "."}
                  </Typography>
                )}
                {session.description && (
                  <Typography sx={{ fontSize: 14.5, color: "#374151", fontFamily: FONT, mt: 1, whiteSpace: "pre-line" }}>
                    {session.description}
                  </Typography>
                )}

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.5 }}>
                  {session.meeting_link && (session.can_join || editor) && ["upcoming", "live"].includes(session.status) && (
                    <Button size="small" variant={session.can_join ? "contained" : "outlined"} color="success"
                      startIcon={<VideoCallOutlinedIcon />} href={session.meeting_link} target="_blank" rel="noopener noreferrer"
                      sx={{ ...btn, boxShadow: "none" }}>
                      Join class
                    </Button>
                  )}
                  {editor && session.meeting_link && ["upcoming", "live"].includes(session.status) && (
                    <Button size="small" startIcon={<ContentCopyIcon />} sx={btn}
                      onClick={() => { navigator.clipboard?.writeText(session.meeting_link); setToast("Meet link copied"); }}>
                      Copy link
                    </Button>
                  )}
                  {editor && session.scheduled && session.status === "upcoming" && (
                    <Button size="small" onClick={() => setDialog("reschedule")} sx={btn}>Reschedule</Button>
                  )}
                  {editor && session.viewers && (
                    <Button size="small" startIcon={<GroupsOutlinedIcon />} onClick={() => setDialog("viewers")} sx={btn}>
                      Watched by {session.viewers.watched}/{session.viewers.students} · avg {session.viewers.average_percent}%
                    </Button>
                  )}
                </Box>

                {upload && (
                  <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontSize: 13, fontFamily: FONT, mb: 0.5 }}>Uploading {upload.name} · {upload.progress}%</Typography>
                    <LinearProgress variant="determinate" value={upload.progress} />
                  </Box>
                )}
                </Box>

                <Box sx={theatre ? { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" } : { mt: 2 }}>
                  {recordings.length > 1 && (
                    <Tabs value={Math.min(part, recordings.length - 1)} onChange={(_, v) => { setPart(v); setJump(null); }}
                      sx={{ mb: 1, minHeight: 36, flexShrink: 0, ...(theatre ? { "& .MuiTab-root": { color: "#cbd5e1" } } : {}) }}>
                      {recordings.map((r) => (
                        <Tab key={r.id} sx={{ textTransform: "none", minHeight: 36, fontFamily: FONT }}
                          label={`Part ${r.part}${r.duration_seconds ? ` · ${formatClock(r.duration_seconds)}` : ""}`} />
                      ))}
                    </Tabs>
                  )}
                  <SessionBody session={session} recording={recording} editor={editor} viewer={viewer} onProgress={onProgress}
                    controlRef={playerRef}
                    startAt={jump && recording && jump.recordingId === recording.id ? jump.t : null}
                    onStarted={() => setJump(null)}
                    theatre={theatre} onTheatre={wide ? toggleTheatre : undefined}
                    onRetry={(r) => run(() => reprocessRecording(r.id), "Queued for processing again")}
                    onRemoveRecording={(r) => run(() => deleteRecording(r.id), "Recording removed")} />
                </Box>
              </Paper>
            </Box>

            {wide && !layout.collapsed && (
              <Divider split={layout.split} containerRef={splitRef} onChange={(split) => updateLayout({ split })} />
            )}

            {/* Folded away: a rail to bring the panel back. */}
            {wide && layout.collapsed && (
              <Paper elevation={0} sx={{ width: 40, flexShrink: 0, borderRadius: 3, border: "1px solid #e5e7eb", position: theatre ? "static" : "sticky", top: 16,
                display: "flex", flexDirection: "column", alignItems: "center", py: 1, gap: 1 }}>
                <Tooltip title="Show the panel" placement="left">
                  <IconButton size="small" onClick={() => updateLayout({ collapsed: false })} aria-label="Show the panel">
                    <ChevronLeftIcon />
                  </IconButton>
                </Tooltip>
                <Box role="button" tabIndex={0} onClick={() => updateLayout({ collapsed: false })}
                  onKeyDown={(e) => { if (e.key === "Enter") updateLayout({ collapsed: false }); }}
                  sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, cursor: "pointer", color: "#0172A6" }}>
                  <ForumOutlinedIcon fontSize="small" />
                  <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, writingMode: "vertical-rl" }}>Ask AI</Typography>
                </Box>
              </Paper>
            )}

            {/* Side: Ask AI, the module's classes, and (mentor) the students' questions.
                Collapsed, it stays mounted (hidden) so a question being answered isn't dropped. */}
            <Box sx={{ flex: 1, minWidth: 0, width: wide ? "auto" : "100%", display: wide && layout.collapsed ? "none" : "block",
              position: wide && !theatre ? "sticky" : "static", top: 16 }}>
              <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e5e7eb", overflow: "hidden",
                display: "flex", flexDirection: "column", height: theatre ? "100%" : wide ? "calc(100vh - 150px)" : "auto", minHeight: wide ? 480 : 0 }}>
                <Box sx={{ px: 1, borderBottom: "1px solid #eef0f2", display: "flex", alignItems: "center" }}>
                  <Tabs value={side} onChange={(_, v) => setSide(v)} variant="fullWidth" sx={{ minHeight: 44, flex: 1 }}>
                    <Tab value="ai" label="Ask AI" sx={{ textTransform: "none", fontFamily: FONT, minHeight: 44 }} />
                    <Tab value="sessions" label="Sessions" sx={{ textTransform: "none", fontFamily: FONT, minHeight: 44 }} />
                    {editor && <Tab value="students" label={oneToOne ? "Student's questions" : "Students' questions"} sx={{ textTransform: "none", fontFamily: FONT, minHeight: 44 }} />}
                  </Tabs>
                  {wide && (
                    <Tooltip title="Hide the panel">
                      <IconButton size="small" onClick={() => updateLayout({ collapsed: true })} aria-label="Hide the panel">
                        <ChevronRightIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                {/* Kept mounted while hidden: switching tabs must not drop a question being answered. */}
                <Box sx={{ display: side === "ai" ? "block" : "none", flex: 1, minHeight: 0 }}>
                  <SessionTutor sessionId={session.id} moduleId={data.module?.id} playerRef={playerRef} onSource={openSource}
                    kind={oneToOne ? "topic" : "template"} />
                </Box>
                {side === "students" && editor && (
                  <Box sx={{ flex: 1, minHeight: 0 }}><StudentQuestions sessionId={session.id} onSource={openSource} /></Box>
                )}
                {side === "sessions" && (
                <Box sx={{ flex: 1, minHeight: 0, maxHeight: wide ? "none" : "70vh", overflowY: "auto" }}>
                  {sessions.map((s) => {
                    const current = s.id === session.id;
                    const percent = watchedPercent(s);
                    return (
                      <Box key={s.id} role="button" tabIndex={0}
                        onClick={() => !current && navigate(sessionPath(s.id))}
                        onKeyDown={(e) => { if (e.key === "Enter" && !current) navigate(sessionPath(s.id)); }}
                        sx={{
                          px: 2, py: 1.25, cursor: current ? "default" : "pointer", borderLeft: "3px solid",
                          borderLeftColor: current ? "#0172A6" : "transparent", backgroundColor: current ? "#eef6fb" : "transparent",
                          "&:hover": { backgroundColor: current ? "#eef6fb" : "#f8fafc" }, borderBottom: "1px solid #f3f4f6",
                        }}>
                        <Typography sx={{ fontFamily: FONT, fontSize: 14, fontWeight: current ? 600 : 500, color: "#1f2937" }}>
                          Session {s.number}{s.title ? ` · ${s.title}` : ""}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.25 }}>
                          <Typography sx={{ fontFamily: FONT, fontSize: 12, color: "#6b7280", flex: 1 }}>
                            {fmtDay(s.starts_at)}{totalLength(s) > 0 ? ` · ${formatClock(totalLength(s))}` : ""} · {STATUS[s.status]?.label}
                          </Typography>
                          {!editor && percent > 0 && (
                            <Typography sx={{ fontFamily: FONT, fontSize: 12, color: "#0172A6" }}>{percent}%</Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
                )}
              </Paper>
            </Box>
          </Box>
        )}
      </Box>

      {session && (
        <>
          <ScheduleDialog open={dialog === "reschedule"} mode="reschedule" session={session} busy={busy} onClose={() => setDialog(null)}
            onSubmit={(form) => run(() => updateSession(session.id, form), "Rescheduled. Attendees were notified.")} />
          <EditSessionDialog open={dialog === "edit"} session={session} busy={busy} onClose={() => setDialog(null)}
            onSubmit={(form) => run(() => updateSession(session.id, form), "Saved")} />
          <ConfirmDialog
            open={dialog === "cancel"}
            title={session.status === "upcoming" ? "Cancel this session?" : "Remove this session?"}
            body={session.status === "upcoming"
              ? (oneToOne ? "The calendar invite is cancelled and the student is notified." : "The calendar invite is cancelled and everyone in the batch is notified.")
              : "The session and its recordings will no longer be visible to students."}
            confirmLabel={session.status === "upcoming" ? "Cancel session" : "Remove"}
            busy={busy}
            onClose={() => setDialog(null)}
            onConfirm={() => run(() => deleteSession(session.id), "Session removed", () => navigate(backPath))}
          />
          {dialog === "viewers" && <ViewersDialog session={session} onClose={() => setDialog(null)} />}
        </>
      )}
      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast("")} message={toast} />
    </Box>
  );
}
